import { Component, ChangeDetectionStrategy, input, output, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import { EChartsOption } from 'echarts';

import { MetricCardComponent } from 'src/app/core/components/metric-card/metric-card.component';
import { StackedBarChartConfig } from 'src/app/core/charts/configs/stacked-bar-chart.config';
import { ChartColors } from 'src/app/core/charts/utils/chart-colors';
import {
  AggregateReport,
  PolicyDocument,
  PolicyGapAnalysisService,
  ControlDomain,
  FINDING_STATUS_CONFIG,
} from '../../services/policy-gap-analysis.service';
import { ReportCardSummaryComponent } from '../report-card-summary/report-card-summary.component';

@Component({
  selector: 'app-aggregate-view',
  standalone: true,
  imports: [CommonModule, NgxEchartsModule, MetricCardComponent, ReportCardSummaryComponent],
  templateUrl: './aggregate-view.component.html',
  styleUrl: './aggregate-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AggregateViewComponent {
  private gapService = inject(PolicyGapAnalysisService);

  aggregateReport = input.required<AggregateReport>();
  documents = input.required<PolicyDocument[]>();

  drillIntoDomain = output<{ domain: ControlDomain }>();

  public analyzedDocCount = computed(() => this.documents().filter((d) => d.status === 'analyzed').length);
  public acknowledgedCount = computed(() => this.gapService.getAcknowledgments()().size);

  public domainChartOptions = computed<EChartsOption>(() => {
    const breakdown = this.aggregateReport().domain_breakdown;
    const categories = breakdown.map((d) => d.display_name);

    return StackedBarChartConfig.create(
      {
        categories,
        series: [
          {
            name: 'Validated',
            data: breakdown.map((d) => Math.round((d.validated / d.total) * 100)),
            color: FINDING_STATUS_CONFIG.validated.color,
          },
          {
            name: 'Not Validated',
            data: breakdown.map((d) => Math.round((d.documented_not_validated / d.total) * 100)),
            color: FINDING_STATUS_CONFIG.documented_not_validated.color,
          },
          {
            name: 'Recommended',
            data: breakdown.map((d) => Math.round((d.recommended_not_documented / d.total) * 100)),
            color: FINDING_STATUS_CONFIG.recommended_not_documented.color,
          },
          {
            name: 'Not Verifiable',
            data: breakdown.map((d) => Math.round((d.not_verifiable / d.total) * 100)),
            color: FINDING_STATUS_CONFIG.not_verifiable.color,
          },
          {
            name: 'Needs More Data',
            data: breakdown.map((d) => Math.round((d.potentially_verifiable / d.total) * 100)),
            color: FINDING_STATUS_CONFIG.potentially_verifiable.color,
          },
        ],
        yAxisTitle: 'Percentage',
      },
      {
        grid: { left: '5%', right: '5%', top: '15%', bottom: '15%', containLabel: true },
        xAxis: {
          type: 'category',
          data: categories,
          axisLabel: { fontSize: 11, rotate: 30 },
        },
      }
    );
  });

  public onDomainClick(domain: ControlDomain): void {
    this.drillIntoDomain.emit({ domain });
  }

  public formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
}
